import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Button,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { apiService } from '../services/api';

export default function AgentConfig() {
  const [config, setConfig] = useState({
    tone: 'friendly',
    style: 'professional and helpful',
    customInstructions: '',
    bookAppointments: true,
    answerQuestions: true,
    processPayments: true,
    sendReminders: true,
    escalateOnComplaint: true,
    escalateOnComplexRequest: true,
    escalateOnCustomerRequest: true,
    followUpEnabled: true,
    daysAfterService: 14,
    maxFollowUps: 3,
    confirmationInstructions: '',
    appointmentRequiresPayment: false,
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadConfig = async () => {
    try {
      const savedConfig = await apiService.getAgentConfig();
      setConfig({ ...config, ...savedConfig });
    } catch (error) {
      console.error('Error loading agent config:', error);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      // Transform flattened config to API structure
      const apiConfig = {
        personality: {
          tone: config.tone as 'professional' | 'friendly' | 'casual' | 'formal',
          style: config.style,
          customInstructions: config.customInstructions,
        },
        capabilities: {
          bookAppointments: config.bookAppointments,
          answerQuestions: config.answerQuestions,
          processPayments: config.processPayments,
          sendReminders: config.sendReminders,
        },
        escalationRules: {
          onComplaint: config.escalateOnComplaint,
          onComplexRequest: config.escalateOnComplexRequest,
          onCustomerRequest: config.escalateOnCustomerRequest,
        },
        followUpSchedule: {
          enabled: config.followUpEnabled,
          daysAfterService: config.daysAfterService,
          maxFollowUps: config.maxFollowUps,
        },
        confirmationInstructions: config.confirmationInstructions,
        appointmentRequiresPayment: config.appointmentRequiresPayment,
      };
      await apiService.saveAgentConfig(apiConfig);
      setSnackbar({ open: true, message: 'Agent configuration saved successfully!', severity: 'success' });
    } catch (error: any) {
      console.error('Error saving agent configuration:', error);
      setSnackbar({ open: true, message: error.message || 'Failed to save configuration', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">AI Agent Configuration</Typography>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Save Configuration'}
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Personality Settings */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Personality & Tone
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Configure how your AI agent communicates with customers
              </Typography>

              <Box display="flex" flexDirection="column" gap={3}>
                <FormControl fullWidth>
                  <InputLabel>Tone</InputLabel>
                  <Select
                    value={config.tone}
                    label="Tone"
                    onChange={(e) => setConfig({ ...config, tone: e.target.value })}
                  >
                    <MenuItem value="professional">Professional</MenuItem>
                    <MenuItem value="friendly">Friendly</MenuItem>
                    <MenuItem value="casual">Casual</MenuItem>
                    <MenuItem value="formal">Formal</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Communication Style"
                  value={config.style}
                  onChange={(e) => setConfig({ ...config, style: e.target.value })}
                  helperText="Describe how the agent should communicate (e.g., 'professional and helpful')"
                />

                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Custom Instructions"
                  value={config.customInstructions}
                  onChange={(e) => setConfig({ ...config, customInstructions: e.target.value })}
                  helperText="Additional instructions for the AI agent (e.g., specific phrases to use, business policies)"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Capabilities */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Capabilities
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                What can the AI agent do?
              </Typography>

              <Box display="flex" flexDirection="column">
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.bookAppointments}
                      onChange={(e) => setConfig({ ...config, bookAppointments: e.target.checked })}
                    />
                  }
                  label="Book Appointments"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.answerQuestions}
                      onChange={(e) => setConfig({ ...config, answerQuestions: e.target.checked })}
                    />
                  }
                  label="Answer Questions"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.processPayments}
                      onChange={(e) => setConfig({ ...config, processPayments: e.target.checked })}
                    />
                  }
                  label="Process Payments"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.sendReminders}
                      onChange={(e) => setConfig({ ...config, sendReminders: e.target.checked })}
                    />
                  }
                  label="Send Reminders"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Escalation Rules */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Escalation Rules
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                When should the AI escalate to a human?
              </Typography>

              <Box display="flex" flexDirection="column">
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.escalateOnComplaint}
                      onChange={(e) => setConfig({ ...config, escalateOnComplaint: e.target.checked })}
                    />
                  }
                  label="On Customer Complaint"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.escalateOnComplexRequest}
                      onChange={(e) => setConfig({ ...config, escalateOnComplexRequest: e.target.checked })}
                    />
                  }
                  label="On Complex Request"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.escalateOnCustomerRequest}
                      onChange={(e) => setConfig({ ...config, escalateOnCustomerRequest: e.target.checked })}
                    />
                  }
                  label="When Customer Requests Human"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Follow-up Schedule - Nail Salon Use Case */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Automated Follow-up Schedule
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Configure when the AI agent should follow up with customers after service
              </Typography>

              <Box display="flex" flexDirection="column" gap={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.followUpEnabled}
                      onChange={(e) => setConfig({ ...config, followUpEnabled: e.target.checked })}
                    />
                  }
                  label="Enable Automated Follow-ups"
                />

                {config.followUpEnabled && (
                  <>
                    <TextField
                      type="number"
                      label="Days After Service"
                      value={config.daysAfterService}
                      onChange={(e) => setConfig({ ...config, daysAfterService: parseInt(e.target.value) })}
                      helperText="How many days after service should the AI agent follow up? (e.g., 14-21 days for nail salon)"
                      InputProps={{ inputProps: { min: 1, max: 90 } }}
                    />

                    <TextField
                      type="number"
                      label="Maximum Follow-ups"
                      value={config.maxFollowUps}
                      onChange={(e) => setConfig({ ...config, maxFollowUps: parseInt(e.target.value) })}
                      helperText="Maximum number of follow-up attempts per customer"
                      InputProps={{ inputProps: { min: 1, max: 10 } }}
                    />
                  </>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Confirmation Settings */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Confirmation Messages
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Configure confirmation messages sent after orders and appointments
              </Typography>

              <Box display="flex" flexDirection="column" gap={3}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Confirmation Instructions"
                  value={config.confirmationInstructions}
                  onChange={(e) => setConfig({ ...config, confirmationInstructions: e.target.value })}
                  helperText="Short instructions sent after payment/appointment booking (e.g., 'We'll notify you when it's ready' or 'See you at 123 Main St')"
                  placeholder="We'll notify you when it's ready."
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={config.appointmentRequiresPayment}
                      onChange={(e) => setConfig({ ...config, appointmentRequiresPayment: e.target.checked })}
                    />
                  }
                  label="Appointments Require Payment Before Confirmation"
                />
                <Typography variant="body2" color="text.secondary">
                  If enabled, appointments are only confirmed after payment is received. If disabled, appointments are confirmed immediately.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Testing Playground */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Test Your AI Agent
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Try out your AI agent configuration before going live
              </Typography>
              <Button variant="outlined" disabled>
                Open Testing Playground (Coming Soon)
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

